/** Offset between server clock and this device's clock, in milliseconds. */
export function serverOffset(serverTime: string, receivedAt = Date.now()): number {
  const parsed = Date.parse(serverTime)
  if (!Number.isFinite(parsed)) return 0
  return parsed - receivedAt
}

/** Remaining milliseconds according to the server clock. */
export function remainingMs(deadline: string, offset: number, now = Date.now()): number {
  const parsed = Date.parse(deadline)
  if (!Number.isFinite(parsed)) return 0
  return Math.max(0, parsed - (now + offset))
}

/** HH:MM:SS for an hour or more, otherwise MM:SS. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const pad = (value: number) => String(value).padStart(2, '0')
  if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
  return `${pad(minutes)}:${pad(seconds)}`
}

export function formatClock(value: Date | string | null | undefined): string {
  if (!value) return '—'
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date)
}

export function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return '—'
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('id-ID', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(date)
}

export function formatExamDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.round((seconds % 3600) / 60)
  if (hours > 0 && minutes > 0) return `${hours} jam ${minutes} menit`
  if (hours > 0) return `${hours} jam`
  return `${minutes} menit`
}

/** Millisecond delay before the next retry, capped at 15 seconds. */
export function retryDelay(attempt: number): number {
  return Math.min(15_000, 1_000 * 2 ** Math.max(0, attempt - 1))
}

/**
 * Retry delay with jitter, in milliseconds. Picks a value between 50% and 100% of
 * the same exponential ceiling as retryDelay (cap 15 s) so that many clients that
 * failed together do not retry together. A server Retry-After (seconds) is honoured
 * when it is larger. `random` is injectable for tests.
 */
export function retryDelayJittered(
  attempt: number,
  random: () => number = Math.random,
  retryAfterSeconds: number | null = null,
): number {
  const ceiling = retryDelay(attempt)
  const r = Math.min(1, Math.max(0, random()))
  const jittered = Math.round(ceiling * (0.5 + 0.5 * r))
  const serverMs = retryAfterSeconds !== null && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 0
  return Math.max(jittered, serverMs)
}
